import React from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'

import { CreateInvoiceRequest } from '../../../hooks/useBillingGroups'
import { BillingGroup } from '../../../models/Billing'

interface CreateInvoiceModalProps {
    open: boolean
    billingGroup: BillingGroup | null
    onClose: () => void
    onCreate: (data: CreateInvoiceRequest) => Promise<void>
}

export const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
    open,
    billingGroup,
    onClose,
    onCreate,
}) => {
    const theme = useTheme()
    const [issueDate, setIssueDate] = React.useState('')
    const [note, setNote] = React.useState('')
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!billingGroup) return

        setIsSubmitting(true)
        try {
            const requestData: CreateInvoiceRequest = {
                billing_group_id: billingGroup.id,
            }

            if (issueDate) {
                requestData.issue_date = issueDate
            }

            if (note.trim()) {
                requestData.note = note.trim()
            }

            await onCreate(requestData)
            handleClose()
        } catch (error) {
            // Error is handled in the hook with toast
            console.error('Failed to create invoice:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setIssueDate('')
        setNote('')
        setIsSubmitting(false)
        onClose()
    }

    const today = new Date().toISOString().split('T')[0]

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: theme.spacing(1.5),
                },
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle
                    style={{
                        padding: theme.spacing(2, 3, 1, 3),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Create Invoice
                    </Typography>
                    {billingGroup && (
                        <Typography
                            variant="body2"
                            style={{
                                color: theme.palette.text.secondary,
                                marginTop: theme.spacing(0.5),
                            }}
                        >
                            {billingGroup.quickbooks_customer.display_name}
                        </Typography>
                    )}
                </DialogTitle>

                <DialogContent style={{ padding: theme.spacing(3) }}>
                    <div style={{ marginBottom: theme.spacing(2) }}>
                        <TextField
                            label="Issue Date"
                            type="date"
                            value={issueDate}
                            onChange={(e) => setIssueDate(e.target.value)}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: today,
                            }}
                            helperText="Select today or a future date"
                        />
                    </div>

                    <div>
                        <TextField
                            label="Note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={3}
                            placeholder="Optional note for the invoice..."
                        />
                    </div>
                </DialogContent>

                <DialogActions
                    style={{
                        padding: theme.spacing(2, 3),
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        style={{ marginRight: theme.spacing(1) }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        style={{
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        {isSubmitting ? 'Creating...' : 'Create Invoice'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
